import { useEffect, useState } from "react";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { anchorHandler, scrollToId } from "@/lib/scroll";

export function useLenis() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ autoRaf: true, lerp: 0.085, smoothWheel: true });
    window.__lenis = lenis;
    return () => { lenis.destroy(); window.__lenis = null; };
  }, []);
}

export function useAnchorScroll() {
  useEffect(() => {
    document.addEventListener("click", anchorHandler);
    const hash = window.location.hash.slice(1);
    const t = hash ? setTimeout(() => scrollToId(hash), 400) : null;
    return () => { document.removeEventListener("click", anchorHandler); if (t) clearTimeout(t); };
  }, []);
}

export function useReveal() {
  useEffect(() => {
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add("show"); io.unobserve(e.target); }
    }), { threshold: 0.11 });
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

export function useSectionPointer() {
  useEffect(() => {
    const move = (e) => {
      const sec = e.target.closest && e.target.closest("section");
      if (!sec) return;
      const r = sec.getBoundingClientRect();
      sec.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
      sec.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
    };
    document.addEventListener("pointermove", move, { passive: true });
    return () => document.removeEventListener("pointermove", move);
  }, []);
}

export function useScrollMeta() {
  const [progress, setProgress] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? (window.scrollY / h) * 100 : 0);
      setScrolled(window.scrollY > 25);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return { progress, scrolled };
}

export function useRailIndex() {
  const [state, setState] = useState({ index: 0, sections: [] });
  useEffect(() => {
    const els = [...document.querySelectorAll("[data-rail]")];
    const sections = els.map((el) => ({ el, label: el.dataset.rail || "Section" }));
    setState({ index: 0, sections });
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) setState((s) => ({ ...s, index: els.indexOf(e.target) }));
    }), { rootMargin: "-45% 0px -45% 0px", threshold: 0 });
    els.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);
  return state;
}

export function useActiveSection(ids) {
  const [active, setActive] = useState("");
  useEffect(() => {
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) setActive(e.target.id);
      else setActive((prev) => (prev === e.target.id ? "" : prev));
    }), { rootMargin: "-35% 0px -55% 0px", threshold: 0 });
    els.forEach((el) => io.observe(el));
    const top = () => { if (window.scrollY < 300) setActive(""); };
    window.addEventListener("scroll", top, { passive: true });
    return () => { io.disconnect(); window.removeEventListener("scroll", top); };
  }, [ids]);
  return active;
}
